import { create } from 'zustand';
import { userApi, auctionApi, cardApi, storeApi, notificationApi, homeApi, adminApi } from '@/utils/api';
import { getCached, setCached, isCacheFresh, getAny, TTL } from '@/utils/cache';

// --- Interfaces ---

export interface User {
    id: number;
    telegram_id: string;
    username: string;
    first_name: string;
    last_name: string;
    is_premium: boolean;
    photo_url: string;
    wallet_address: string;
    role: string;

    // Stats & Level
    cardCount: number;
    level: number;
    xp_progress: number;
    next_level_xp: number;
    current_xp: number;
    is_new?: boolean;
    created_at?: string;
    profile_photo_big?: string;
    custom_emoji_status?: string;
    friendCount?: number;
    friendPreview?: string[];
}

export interface Wallet {
    available_balance: number;
    locked_balance: number;
    total_deposited: number;
    total_withdrawn: number;
    total_purchased: number;
    total_earned: number;
    net_profit: number;
    platform_fee_discount: number;
}

export interface Card {
    card_id: number;
    card_uuid: string;
    serial_number: number;
    card_status: string;
    current_value: number;
    definition_id: number;
    name: string;
    image_filename: string;
    rarity: string;
    rank: string;
    base_price: number;
    background_color?: string;
    text_color?: string;
    listing_price?: number;
    listing_type?: string;
}

export interface Auction {
    id: number;
    card_id: number;
    seller_name: string;
    seller_avatar: string;
    name: string;
    current_bid: number;
    image_filename: string;
    base_color?: string;
    text_color?: string;
    likes_count: number;
    is_liked: boolean;
    timeLeftSeconds: number;
    end_time: string;
    isPending: boolean;
    asset_type: string;
    verified_username?: string;
}

export interface MarketListing {
    listing_id: number;
    card_id: number;
    seller_id: number;
    price: number;
    type: string; // 'direct', 'auction'
    status: string;
}

export interface Transaction {
    id: number;
    user_id: number;
    type: 'deposit' | 'withdraw' | 'purchase' | 'sale' | 'fee';
    amount: string | number;
    status: 'pending' | 'completed' | 'failed';
    tx_hash?: string;
    metadata?: any;
    created_at: string;
}

export interface GlobalState {
    user: User | null;
    wallet: Wallet;
    notifications: any[];

    // Feature Stores
    activeAuctions: Auction[];
    userCards: Card[];
    marketListings: any[];
    marketItems: any[];
    transactions: Transaction[];
    tileBgs: Record<string, string>;

    loading: boolean;
    pageLoading: boolean;

    // Preferences
    hapticsEnabled: boolean;
    animationsEnabled: boolean;
    currentRoute: string;

    // ─── Cache Timestamps ───
    lastFetched: Record<string, number>;

    // Actions
    setUser: (user: User | null) => void;
    setWallet: (wallet: Partial<Wallet>) => void;
    setNotifications: (notifications: any[]) => void;
    setLoading: (loading: boolean) => void;
    setPageLoading: (loading: boolean) => void;
    setHapticsEnabled: (enabled: boolean) => void;
    setAnimationsEnabled: (enabled: boolean) => void;
    setCurrentRoute: (route: string) => void;

    // Cache helpers
    isFresh: (key: string, ttlMs: number) => boolean;
    markFetched: (key: string) => void;

    // Async Actions
    fetchUser: (telegramId: string | number, forceRefresh?: boolean) => Promise<void>;
    hydrateFromCache: () => void;
    fetchTransactions: () => Promise<void>;
    fetchAuctions: (forceRefresh?: boolean) => Promise<void>;
    fetchFinishedAuctions: () => Promise<void>;
    fetchSoldItems: () => Promise<void>;
    fetchUserCards: (forceRefresh?: boolean) => Promise<void>;
    fetchNotifications: () => Promise<void>;
    fetchListings: (page?: number, forceRefresh?: boolean) => Promise<void>;
    fetchMarketItems: (filter?: string, userId?: string | number, packageId?: string | number, signal?: AbortSignal) => Promise<void>;
    fetchTileBgs: (forceRefresh?: boolean) => Promise<void>;
    fetchAnimationConfig: () => Promise<void>;
    clearMarket: () => void;
    toggleLike: (cardId: number | string) => Promise<void>;
}

export const useStore = create<GlobalState>((set, get) => ({
    user: null,
    wallet: {
        available_balance: 0,
        locked_balance: 0,
        total_deposited: 0,
        total_withdrawn: 0,
        total_purchased: 0,
        total_earned: 0,
        net_profit: 0,
        platform_fee_discount: 0
    },
    notifications: [],
    tileBgs: getAny<Record<string, string>>('tile_bgs') || {},
    activeAuctions: [],
    userCards: [],
    marketListings: [],
    marketItems: [],
    transactions: [],
    loading: true,
    pageLoading: false,
    hapticsEnabled: true, // defaulting to true
    animationsEnabled: true, // defaulting to true
    currentRoute: 'store',
    lastFetched: {},

    // ─── Cache Helpers ───
    isFresh: (key, ttlMs) => {
        const ts = get().lastFetched[key] || 0;
        return ts > 0 && Date.now() - ts < ttlMs;
    },
    markFetched: (key) => {
        set(state => ({ lastFetched: { ...state.lastFetched, [key]: Date.now() } }));
    },

    setUser: (user) => set({ user }),
    setWallet: (walletUpdate) => set((state) => ({
        wallet: { ...state.wallet, ...walletUpdate }
    })),
    setNotifications: (notifications) => set({ notifications }),
    setLoading: (loading) => set({ loading }),
    setPageLoading: (pageLoading) => set({ pageLoading }),
    setHapticsEnabled: (hapticsEnabled) => {
        set({ hapticsEnabled });
        setCached('haptics_pref', hapticsEnabled);
    },
    setAnimationsEnabled: (animationsEnabled) => {
        set({ animationsEnabled });
        setCached('animations_pref', animationsEnabled);
    },
    setCurrentRoute: (currentRoute) => set({ currentRoute }),

    // ─── Hydrate from localStorage on app open ───
    hydrateFromCache: () => {
        try {
            const cachedUser = getCached<{ user: User; wallet: Wallet }>('user_data', TTL.USER);
            if (cachedUser?.user) {
                set({
                    user: cachedUser.user,
                    wallet: cachedUser.wallet,
                    loading: false,
                });
            }

            const cachedCards = getCached<Card[]>('user_cards', TTL.CARDS);
            if (cachedCards) {
                set({ userCards: cachedCards });
            }

            const cachedAuctions = getCached<Auction[]>('active_auctions', TTL.AUCTIONS);
            if (cachedAuctions) {
                set({ activeAuctions: cachedAuctions });
            }

            const cachedListings = getCached<any[]>('market_listings', TTL.LISTINGS);
            if (cachedListings) {
                set({ marketListings: cachedListings });
            }

            const cachedHaptics = localStorage.getItem('cache_haptics_pref');
            if (cachedHaptics !== null) {
                try {
                    const parsed = JSON.parse(cachedHaptics);
                    if (parsed && typeof parsed.data === 'boolean') {
                        set({ hapticsEnabled: parsed.data });
                    }
                } catch(e) {}
            }

            const cachedAnimations = localStorage.getItem('cache_animations_pref');
            if (cachedAnimations !== null) {
                try {
                    const parsed = JSON.parse(cachedAnimations);
                    if (parsed && typeof parsed.data === 'boolean') {
                        set({ animationsEnabled: parsed.data });
                    }
                } catch(e) {}
            }
        } catch (e) {
            console.warn('[Store] Hydration failed:', e);
        }
    },

    // ─── Fetch User (always fetches fresh, cache used for instant display only) ───
    fetchUser: async (telegramId, forceRefresh = false) => {
        // Don't block if already fetching (debounce rapid calls within 30s)
        const key = 'user';
        if (!forceRefresh && get().isFresh(key, 30 * 1000)) return;

        try {
            const res = await userApi.get(telegramId);
            if (res.ok) {
                const data = await res.json();
                const user: User = {
                    id: data.user_id,
                    telegram_id: data.telegram_id,
                    username: data.username,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    is_premium: !!data.is_premium,
                    photo_url: data.photo_url,
                    wallet_address: data.wallet_address,
                    role: data.role,
                    cardCount: data.cardCount || 0,
                    level: data.level || 1,
                    xp_progress: data.xp_progress || 0,
                    next_level_xp: data.next_level_xp || 100,
                    current_xp: data.current_xp || 0,
                    is_new: !!data.is_new,
                    created_at: data.created_at,
                    friendCount: data.friendCount || 0,
                    friendPreview: data.friendPreview || []
                };
                const wallet: Wallet = {
                    available_balance: data.wallet_balance || 0,
                    locked_balance: data.locked_balance || 0,
                    total_deposited: data.total_deposited || 0,
                    total_withdrawn: data.total_withdrawn || 0,
                    total_purchased: data.total_purchased || 0,
                    total_earned: data.total_earned || 0,
                    net_profit: (data.total_earned || 0) - (data.total_purchased || 0),
                    platform_fee_discount: data.platform_fee_discount || 0
                };

                set({ user, wallet, loading: false });
                get().markFetched(key);

                // Persist to localStorage
                setCached('user_data', { user, wallet });
            }
        } catch (e) {
            console.error('Fetch User Error:', e);
            set({ loading: false });
        }
    },

    fetchTransactions: async () => {
        const { user } = get();
        if (!user) return;
        try {
            const res = await userApi.getTransactions(user.telegram_id);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    set({ transactions: data });
                }
            }
        } catch (e) {
            console.error('Fetch Transactions Error:', e);
        }
    },

    fetchListings: async (page = 1, forceRefresh = false) => {
        const key = 'listings';
        if (!forceRefresh && page === 1 && get().isFresh(key, TTL.LISTINGS)) return;

        try {
            const { user, marketListings } = get();
            const res = await storeApi.getListings(user?.telegram_id, page);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    if (page === 1 || forceRefresh) {
                        set({ marketListings: data });
                        get().markFetched(key);
                        setCached('market_listings', data);
                    } else {
                        const existingIds = new Set(marketListings.map((l: any) => l.id));
                        const newUnique = data.filter((l: any) => !existingIds.has(l.id));
                        set({ marketListings: [...marketListings, ...newUnique] });
                    }
                }
            }
        } catch (e) {
            console.error('Fetch Listings Error:', e);
        }
    },

    fetchMarketItems: async (filter, userId, packageId, signal) => {
        try {
            const uidStr = userId ? String(userId) : undefined;
            const res = await storeApi.getMarketItems(filter, uidStr, packageId, signal);
            if (res.ok) {
                const data = await res.json();
                if (data.success && Array.isArray(data.items)) {
                    set({ marketItems: data.items });
                }
            }
        } catch (e: any) {
            if (e.name === 'AbortError') return;
            console.error('Fetch Market Items Error:', e);
        }
    },

    // ─── Fetch Auctions (with cache + staleTime) ───
    fetchAuctions: async (forceRefresh = false) => {
        const key = 'auctions';
        if (!forceRefresh && get().isFresh(key, TTL.AUCTIONS)) return;

        const { user } = get();
        try {
            const res = await auctionApi.getActive(user?.telegram_id);
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    set({ activeAuctions: data.auctions });
                    get().markFetched(key);
                    setCached('active_auctions', data.auctions);
                }
            }
        } catch (e) {
            console.error('Fetch Auctions Error:', e);
        }
    },

    fetchFinishedAuctions: async () => {
        const { user } = get();
        try {
            const res = await auctionApi.getFinished(user?.telegram_id);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    setCached('finished_auctions', data);
                }
            }
        } catch (e) {
            console.error('Fetch Finished Auctions Error:', e);
        }
    },

    fetchSoldItems: async () => {
        try {
            const res = await homeApi.getRecentlySold();
            if (res.ok) {
                const data = await res.json();
                if (data.success && Array.isArray(data.items)) {
                    setCached('sold_items', data.items);
                }
            }
        } catch (e) {
            console.error('Fetch Sold Items Error:', e);
        }
    },

    // ─── Fetch User Cards (with cache) ───
    fetchUserCards: async (forceRefresh = false) => {
        const { user } = get();
        if (!user) return;

        const key = 'userCards';
        if (!forceRefresh && get().isFresh(key, TTL.CARDS)) return;

        try {
            const res = await cardApi.getUserCards(user.telegram_id);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    set({ userCards: data });
                    get().markFetched(key);
                    setCached('user_cards', data);
                }
            }
        } catch (e) {
            console.error('Fetch User Cards Error:', e);
        }
    },

    fetchNotifications: async () => {
        try {
            const res = await notificationApi.getAll();
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    set({ notifications: data });
                }
            }
        } catch (e) {
            console.error('Fetch Notifications Error:', e);
        }
    },

    toggleLike: async (cardId: number | string) => {
        const { user, activeAuctions, marketListings, marketItems, userCards } = get();
        if (!user) return;

        const updateList = (list: any[]) => {
            return list.map(item => {
                const itemId = item.card_id || item.id || item.listing_id;
                if (String(itemId) === String(cardId)) {
                    const currentIsLiked = !!(item.isLiked ?? item.is_liked);
                    const currentLikes = Number(item.likesCount ?? item.likes_count ?? 0);
                    const nextIsLiked = !currentIsLiked;
                    const nextLikes = nextIsLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);
                    return {
                        ...item,
                        isLiked: nextIsLiked,
                        is_liked: nextIsLiked,
                        likesCount: nextLikes,
                        likes_count: nextLikes
                    };
                }
                return item;
            });
        };

        set({
            activeAuctions: updateList(activeAuctions),
            marketListings: updateList(marketListings),
            marketItems: updateList(marketItems),
            userCards: updateList(userCards)
        });

        try {
            await cardApi.like(Number(cardId), user.telegram_id);
        } catch (e) {
            console.error('Like Error:', e);
            const freshState = get();
            set({
                activeAuctions: updateList(freshState.activeAuctions),
                marketListings: updateList(freshState.marketListings),
                marketItems: updateList(freshState.marketItems),
                userCards: updateList(freshState.userCards)
            });
        }
    },

    clearMarket: () => set({
        marketItems: [],
        marketListings: [],
        activeAuctions: []
    }),

    fetchTileBgs: async (forceRefresh = false) => {
        const key = 'tileBgs';
        const { tileBgs, isFresh, markFetched } = get();
        
        // Prevent re-fetching if already in memory and fresh
        if (!forceRefresh && Object.keys(tileBgs).length > 0 && isFresh(key, TTL.SKINS)) return;

        try {
            const res = await homeApi.getProfileTileBgs();
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.backgrounds) {
                    set({ tileBgs: data.backgrounds });
                    markFetched(key);
                    setCached('tile_bgs', data.backgrounds);
                }
            }
        } catch (e) {
            console.error('Fetch Tile Bgs Error:', e);
        }
    },

    fetchAnimationConfig: async () => {
        try {
            const res = await adminApi.getAnimationConfig();
            if (res.ok) {
                const data = await res.json();
                if (data.success && typeof data.enabled === 'boolean') {
                    set({ animationsEnabled: data.enabled });
                    setCached('animations_pref', data.enabled);
                }
            }
        } catch (e) {
            console.error('Fetch Animation Config Error:', e);
        }
    },
}));
